/** Pretty-format a sensor value for display. `decimals` caps the
 *  fractional digits (default 3); trailing zeros are stripped so 23.0
 *  reads as "23" and 23.225 at decimals=1 reads as "23.2". The history
 *  band panel + sensor table pass decimals=1 for compact "direct
 *  reading" legibility; live cards keep the default. */
export function fmt(val: string | number | null | undefined, decimals = 3): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') return val.toFixed(decimals).replace(/\.?0+$/, '');
  return String(val).replace(/^"|"$/g, '');
}

/** Time formatter for chart axis / log timestamps.
 *
 * Defaults are "smart": when `ts` falls on a different calendar day than
 * "now", the formatter prepends `MM-DD` so a stale row mid-stream
 * doesn't read like it just arrived. `showDate: true` forces the date
 * regardless; `ms: true` appends millisecond precision.
 *
 * Output uses ISO-style separators (`-` for dates, `:` for times) and
 * fixed-width zero padding so the column stays legible in a tabular
 * monospace context. */
export function formatTs(ts: number, opts: { showDate?: boolean; ms?: boolean } = {}): string {
  const d = new Date(ts);
  const HH = String(d.getHours()).padStart(2, '0');
  const MM = String(d.getMinutes()).padStart(2, '0');
  const SS = String(d.getSeconds()).padStart(2, '0');
  const time = `${HH}:${MM}:${SS}`;
  const withMs = opts.ms ? `${time}.${String(d.getMilliseconds()).padStart(3, '0')}` : time;

  const showDate = opts.showDate ?? !sameLocalDay(d, new Date());
  if (showDate) {
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${mo}-${da} ${withMs}`;
  }
  return withMs;
}

function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth()    === b.getMonth()
    && a.getDate()     === b.getDate();
}

/** Human relative time. */
export function relTime(ts: number, now: number): string {
  const s = Math.floor((now - ts) / 1000);
  if (s < 5)    return 'live';
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}
